import {
  API_BASE_URL,
  API_ENDPOINT_CREATE_GROUP,
  API_ENDPOINT_CREATE_USER,
  API_ENDPOINT_DELETE_GROUP,
  API_ENDPOINT_DELETE_USER,
  API_ENDPOINT_EDIT_GROUP,
  API_ENDPOINT_EDIT_USER,
  API_ENDPOINT_GET_GROUPS,
  API_ENDPOINT_GET_REPORTS,
  API_ENDPOINT_GET_USERS,
  API_ENDPOINT_VERIFY_USER,
  API_ENDPOINT_PASSWORD_RESET,
  API_ENDPOINT_PASSWORD_UPDATE,
} from "../utils/Constants";
import { getAuth, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { initializeApp } from "firebase/app";
import { UtilFunctions } from "../utils/UtilFunctions";

export namespace APIService {
  // return reports array if successfully fetched reports, null otherwise
  export async function fetchReports(id: string, jwt: string) {
    try {
      const postBody = {
        userAdminID: id,
        token: jwt,
        scope: "all",
      };
      const options = {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(postBody),
      };
      const res = await fetch(API_BASE_URL + API_ENDPOINT_GET_REPORTS, options);
      const json = await res.json();
      const mappedReports = UtilFunctions.mapReports(json.reports);
      return mappedReports;
    } catch (e) {
      console.error("Failed to fetch reports: ", e);
      return null;
    }
  }

  export async function getGroups(jwt: string) {
    try {
      const postBody = {
        token: jwt,
      };
      const options = {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(postBody),
      };
      const res = await fetch(API_BASE_URL + API_ENDPOINT_GET_GROUPS, options);
      const json = await res.json();
      return json.body;
    } catch (e) {
      console.error("Failed to get groups: ", e);
      return null;
    }
  }

  export async function editGroup(jwt: string, newGroupName: string, groupId: string) {
    try {
      const postBody = {
        token: jwt,
        NewGroupName: newGroupName,
        groupId: groupId,
      };
      const options = {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(postBody),
      };
      const res = await fetch(API_BASE_URL + API_ENDPOINT_EDIT_GROUP, options);
      const json = await res.json();
      return json.success;
    } catch (e) {
      console.error("Failed to edit group: ", e);
      return false;
    }
  }

  export async function createGroup(jwt: string, newGroupName: string) {
    try {
      const postBody = {
        token: jwt,
        NewGroupName: newGroupName,
      };
      const options = {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(postBody),
      };
      const res = await fetch(API_BASE_URL + API_ENDPOINT_CREATE_GROUP, options);
      const json = await res.json();
      return json.success;
    } catch (e) {
      console.error("Failed to edit group: ", e);
      return false;
    }
  }

  // returns true if successful, false otherwise
  export async function deleteGroup(jwt: string, groupId: string) {
    try {
      const postBody = {
        token: jwt,
        groupId: groupId,
      };
      const options = {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(postBody),
      };
      const res = await fetch(API_BASE_URL + API_ENDPOINT_DELETE_GROUP, options);
      const json = await res.json();
      return json.success;
    } catch (e) {
      console.error("Failed to delete group: ", e);
      return false;
    }
  }

  export async function fetchUsers(jwt: string) {
    try {
      const postBody = {
        token: jwt,
      };
      const options = {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(postBody),
      };
      const res = await fetch(API_BASE_URL + API_ENDPOINT_GET_USERS, options);
      const json = await res.json();
      return UtilFunctions.mapUsers(json.body);
    } catch (e) {
      console.error("Failed to fetch users: ", e);
      return null;
    }
  }

  // returns true if successful, false otherwise
  export async function deleteUser(email: string, jwt: string) {
    try {
      const postBody = {
        token: jwt,
        email: email,
      };
      const options = {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(postBody),
      };
      const res = await fetch(API_BASE_URL + API_ENDPOINT_DELETE_USER, options);
      const json = await res.json();
      return json.success;
    } catch (e) {
      console.error("Failed to delete user: ", e);
      return false;
    }
  }

  export async function createUser(
    jwt: string,
    email: string,
    groups: string,
    surname: string,
    forename: string,
    area: string,
    weeklyReports: number
  ) {
    try {
      const postBody = {
        token: jwt,
        email: email,
        Groups: groups,
        Surname: surname,
        Forename: forename,
        Area: area,
        WeeklyReports: weeklyReports,
      };
      // console.log("sending createUser request with body: ", JSON.stringify(postBody));
      const options = {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(postBody),
      };
      const res = await fetch(API_BASE_URL + API_ENDPOINT_CREATE_USER, options);
      const json = await res.json();
      return json.success;
    } catch (e) {
      console.error("Failed to create a user: ", e);
      return false;
    }
  }

  export async function editUser(
    jwt: string,
    id: string,
    groups: string,
    displayName: string,
    area: string,
    weeklyReports: number
  ) {
    try {
      const postBody = {
        token: jwt,
        id: id,
        Groups: groups,
        displayName: displayName,
        Area: area,
        WeeklyReports: weeklyReports,
      };
      // console.log("sending editUser request with body: ", JSON.stringify(postBody));
      const options = {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(postBody),
      };
      const res = await fetch(API_BASE_URL + API_ENDPOINT_EDIT_USER, options);
      const json = await res.json();
      return json.success;
    } catch (e) {
      console.error("Failed to edit user: ", e);
      return false;
    }
  }

  // returns the user object if login was successful, null otherwise
  export async function login(email: string, password: string) {
    try {
      const auth = getAuth();
      const response = await signInWithEmailAndPassword(auth, email, password);
      return response.user;
    } catch (e) {
      console.error("Failed to login: ", e);
      return null;
    }
  }

  export async function signOutUser() {
    try {
      const auth = getAuth();
      await signOut(auth);
      return true;
    } catch (e) {
      console.error("Failed to sign out: ", e);
      return false;
    }
  }

  // return verified user if successfull, null otherwise
  export async function verifyUser(id: string, email: string) {
    try {
      const postBody = {
        FB_UUID: id,
        email: email,
      };
      const options = {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(postBody),
      };
      const res = await fetch(API_BASE_URL + API_ENDPOINT_VERIFY_USER, options);
      const json = await res.json();
      if (json.success) {
        return json;
      } else {
        return null;
      }
    } catch (e) {
      console.error("Failed to verify user: ", e);
      return null;
    }
  }

  // sends password reset email and returns true if successful, false otherwise
  export async function requestPasswordReset(email: string) {
    try {
      const options = {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: email }),
      };
      const res = await fetch(API_BASE_URL + API_ENDPOINT_PASSWORD_RESET, options);
      const json = await res.json();
      return json.success;
    } catch (e) {
      console.error("Failed to send password reset email: ", e);
      return false;
    }
  }

  // updates user's password and returns true if successful, false otherwise
  export async function updatePassword(token: string, newPassword: string) {
    try {
      const options = {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token, newPassword }),
      };
      const res = await fetch(API_BASE_URL + API_ENDPOINT_PASSWORD_UPDATE, options);
      console.log("res: ", res);
      const json = await res.json();
      console.log("json: ", json);
      return json.success;
    } catch (e) {
      console.error("Failed to update password: ", e);
      return false;
    }
  }

  // initialize firebase
  export function initFirebase() {
    // this is client side firebase config, do not use in development
    // const firebaseConfig = {
    //   apiKey: "AIzaSyCvzsiFH0cVGFZoybwYPpdaTV31IgdjzWg",
    //   authDomain: "safecarwash-77e75.firebaseapp.com",
    //   projectId: "safecarwash-77e75",
    //   storageBucket: "safecarwash-77e75.appspot.com",
    //   messagingSenderId: "809103925360",
    //   appId: "1:809103925360:web:59ca742ff2bce9fb8dd4b5",
    //   measurementId: "G-MWMTC4LG2T",
    // };\
    const firebaseConfig = {
      apiKey: "AIzaSyAGeHe2Taiy7IGqgQ8e5IMfELAw-Qk5KHQ",
      authDomain: "safe-car-wash-1fab3.firebaseapp.com",
      projectId: "safe-car-wash-1fab3",
      storageBucket: "safe-car-wash-1fab3.firebasestorage.app",
      messagingSenderId: "1023336871218",
      appId: "1:1023336871218:web:4293ab7117e7f4c896532b",
      measurementId: "G-P92GK5VCKM"
    };
    initializeApp(firebaseConfig);
  }
}
